import { useState, useEffect } from 'react';
import axios from 'axios';
import EmployeeLayout from '../../components/layout/EmployeeLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Banknote, Eye, Download, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyPayslipsPage() {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/me/payslips`)
      .then((r) => setSlips(r.data.payslips || []))
      .catch(() => toast.error('Could not load payslips'))
      .finally(() => setLoading(false));
  }, []);

  const fetchBlob = async (p) => {
    const res = await axios.get(`${API}/me/payslips/${p.id}/download`, { responseType: 'blob' });
    return window.URL.createObjectURL(res.data);
  };

  const view = async (p) => {
    try { window.open(await fetchBlob(p), '_blank'); }
    catch { toast.error('Could not open payslip'); }
  };

  const download = async (p) => {
    try {
      const url = await fetchBlob(p);
      const a = document.createElement('a');
      a.href = url;
      const ext = (p.content_type || '').includes('pdf') ? 'pdf' : (p.content_type || '').includes('png') ? 'png' : 'jpg';
      a.download = `payslip_${p.month}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download payslip'); }
  };

  return (
    <EmployeeLayout>
      <div className="space-y-6" data-testid="my-payslips-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
            <Banknote className="w-6 h-6 text-primary" /> My Payslips
          </h1>
          <p className="text-sm text-muted-foreground">View and download your monthly payslips.</p>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
        ) : slips.length === 0 ? (
          <Card className="border-border"><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="payslips-empty">
            No payslips available yet.
          </CardContent></Card>
        ) : (
          <Card className="border-border">
            <CardContent className="p-0 divide-y divide-border/50">
              {slips.map((p) => (
                <div key={p.id} className="flex items-center gap-2 px-4 py-3" data-testid={`payslip-row-${p.month}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{p.month_label}</p>
                    <p className="text-[11px] text-muted-foreground">Added {String(p.uploaded_at || '').slice(0, 10)}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => view(p)}
                    className="h-8 rounded-sm text-xs" data-testid={`payslip-view-${p.month}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> View
                  </Button>
                  <Button size="sm" onClick={() => download(p)}
                    className="h-8 rounded-sm text-xs bg-primary text-primary-foreground hover:bg-yellow-300"
                    data-testid={`payslip-download-${p.month}`}>
                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </EmployeeLayout>
  );
}
